import { Nullable } from './types';

declare global {
  interface Window {
    Calendly?: {
      initInlineWidget: (options: {
        url: string;
        parentElement: HTMLElement;
        prefill?: Record<string, string>;
        utm?: Record<string, string>;
      }) => void;
      closePopupWidget: () => void;
      initPopupWidget: (options: { url: string }) => void;
    };
  }
}

export interface CalendlyEventData {
  event: string;
  payload?: {
    event?: {
      uri: string;
    };
    invitee?: {
      uri?: string;
      name?: string;
      email?: string;
    };
  };
}

/**
 * Load Calendly widget script
 */
export function loadCalendlyScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    // Check if script already exists
    if (document.querySelector('script[src*="calendly.com/assets/external/widget.js"]')) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Calendly script'));
    document.head.appendChild(script);

    // Also add Calendly CSS
    if (!document.querySelector('link[href*="calendly.com/assets/external/widget.css"]')) {
      const link = document.createElement('link');
      link.href = 'https://assets.calendly.com/assets/external/widget.css';
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
  });
}

/**
 * Initialize Calendly inline widget
 */
export function initCalendlyInline(
  container: HTMLElement,
  calendlyUrl: string,
  prefill?: { name?: string; email?: string }
): void {
  if (!window.Calendly) {
    console.error('Calendly script not loaded');
    return;
  }

  window.Calendly.initInlineWidget({
    url: calendlyUrl,
    parentElement: container,
    prefill: prefill || {},
  });
}

/**
 * Fetch widget configuration including Calendly URL
 */
export async function fetchWidgetConfig(
  apiBaseUrl: string,
  widgetKey: string
): Promise<{
  calendly_url: Nullable<string>;
  has_calendly: boolean;
  text_handoff_enabled?: boolean;
}> {
  const url = `${apiBaseUrl}/widget/config?widget_key=${encodeURIComponent(widgetKey)}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'x-widget-key': widgetKey,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch widget config: ${response.statusText}`);
  }

  const data = await response.json();
  return data.config || { calendly_url: null, has_calendly: false, text_handoff_enabled: false };
}

/**
 * Log meeting booking to backend
 */
export async function logMeetingBooking(
  apiBaseUrl: string,
  payload: {
    session_id: string;
    widget_key: string;
    event_uri: string;
    invitee_uri?: string;
    invitee_name?: string;
    invitee_email?: string;
    event_start_time?: string;
    event_end_time?: string;
    event_type_name?: string;
  }
): Promise<void> {
  const url = `${apiBaseUrl}/meetings/log`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    console.error('Failed to log meeting booking:', response.statusText);
  }
}
